import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'react-hot-toast';
import {
  PlusIcon,
  PencilIcon,
  TrashIcon,
  EyeIcon,
  EyeSlashIcon,
  ChevronRightIcon,
  ChevronDownIcon,
  Cog6ToothIcon,
  ShieldCheckIcon,
  TagIcon,
  ScaleIcon
} from '@heroicons/react/24/outline';

const AdminCategories = () => {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [expandedCategories, setExpandedCategories] = useState(new Set());
  const [showTransparencyModal, setShowTransparencyModal] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState(null);

  const [formData, setFormData] = useState({
    name: '',
    name_en: '',
    description: '',
    description_en: '',
    slug: '',
    parent_id: null,
    sort_order: 0,
    is_active: true,
    is_featured: false,
    // Transparency & Control
    global_attributes: {},
    default_warranty_period: '',
    default_tax_class: 'standard',
    default_shipping_class: '',
    transparency_settings: {
      showStockToB2B: true,
      showStockToB2C: true,
      showCostToB2B: false,
      showCostToB2C: false,
      showSupplierToB2B: false,
      showMarginToAdmin: true
    },
    compliance_requirements: {},
    auto_apply_tags: [],
    visibility_settings: {
      audience: 'both',
      showInMenu: true,
      showInSearch: true
    },
    require_approval: false,
    min_order_quantity: 1,
    max_order_quantity: null,
    allow_reviews: true,
    allow_negotiations: false,
    default_return_policy: ''
  });

  useEffect(() => {
    fetchCategories();
  }, []);

  const fetchCategories = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get('/api/categories', {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Cache-Control': 'no-cache',
          'Pragma': 'no-cache'
        },
        params: {
          _t: new Date().getTime() // Cache buster
        }
      });
      setCategories(response.data.allCategories || []);
    } catch (error) {
      toast.error('שגיאה בטעינת קטגוריות');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      const dataToSend = {
        ...formData,
        parent_id: formData.parent_id || null,
        default_warranty_period: formData.default_warranty_period ? parseInt(formData.default_warranty_period) : null,
        min_order_quantity: parseInt(formData.min_order_quantity) || 1,
        max_order_quantity: formData.max_order_quantity ? parseInt(formData.max_order_quantity) : null,
      };
      
      if (editingCategory) {
        await axios.put(
          `/api/categories/${editingCategory.id}`,
          dataToSend,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success('הקטגוריה עודכנה בהצלחה');
      } else {
        await axios.post(
          '/api/categories',
          dataToSend,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success('הקטגוריה נוספה בהצלחה');
      }

      setShowModal(false);
      resetForm();
      
      // Small delay to ensure DB transaction completes before fetching
      setTimeout(() => {
        fetchCategories();
      }, 300);
    } catch (error) {
      const errorMsg = error.response?.data?.message || error.response?.data?.errors?.[0]?.msg || 'שגיאה בשמירת הקטגוריה';
      toast.error(errorMsg);
    }
  };

  const handleEdit = (category) => {
    setEditingCategory(category);
    setFormData({
      name: category.name || '',
      name_en: category.name_en || '',
      description: category.description || '',
      description_en: category.description_en || '',
      slug: category.slug || '',
      parent_id: category.parent_id || null,
      sort_order: category.sort_order || 0,
      is_active: category.is_active !== false,
      is_featured: category.is_featured || false,
      global_attributes: category.global_attributes || {},
      default_warranty_period: category.default_warranty_period || '',
      default_tax_class: category.default_tax_class || 'standard',
      default_shipping_class: category.default_shipping_class || '',
      transparency_settings: category.transparency_settings || {
        showStockToB2B: true,
        showStockToB2C: true,
        showCostToB2B: false,
        showCostToB2C: false,
        showSupplierToB2B: false,
        showMarginToAdmin: true
      },
      compliance_requirements: category.compliance_requirements || {},
      auto_apply_tags: category.auto_apply_tags || [],
      visibility_settings: category.visibility_settings || {
        audience: 'both',
        showInMenu: true,
        showInSearch: true
      },
      require_approval: category.require_approval || false,
      min_order_quantity: category.min_order_quantity || 1,
      max_order_quantity: category.max_order_quantity || null,
      allow_reviews: category.allow_reviews !== false,
      allow_negotiations: category.allow_negotiations || false,
      default_return_policy: category.default_return_policy || ''
    });
    setShowModal(true);
  };

  const handleDelete = async (categoryId) => {
    if (!window.confirm('האם אתה בטוח שברצונך למחוק את הקטגוריה?')) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      await axios.delete(
        `/api/categories/${categoryId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('הקטגוריה נמחקה בהצלחה');
      
      // Small delay to ensure DB transaction completes
      setTimeout(() => {
        fetchCategories();
      }, 300);
    } catch (error) {
      toast.error('שגיאה במחיקת הקטגוריה');
    }
  };

  const toggleActive = async (category) => {
    try {
      const token = localStorage.getItem('token');
      
      // Only send the fields that should be updated, not the entire object
      const updateData = {
        name: category.name,
        name_en: category.name_en,
        description: category.description,
        description_en: category.description_en,
        slug: category.slug,
        parent_id: category.parent_id || null,
        sort_order: category.sort_order || 0,
        is_active: !category.is_active, // Toggle the status
        is_featured: category.is_featured || false,
        meta_title: category.meta_title,
        meta_description: category.meta_description,
        // Transparency & Control fields
        global_attributes: category.global_attributes || {},
        default_warranty_period: category.default_warranty_period || null,
        default_tax_class: category.default_tax_class || 'standard',
        default_shipping_class: category.default_shipping_class || null,
        transparency_settings: category.transparency_settings || {},
        compliance_requirements: category.compliance_requirements || {},
        auto_apply_tags: category.auto_apply_tags || [],
        visibility_settings: category.visibility_settings || { audience: 'both', showInMenu: true, showInSearch: true },
        require_approval: category.require_approval || false,
        min_order_quantity: category.min_order_quantity || 1,
        max_order_quantity: category.max_order_quantity || null,
        allow_reviews: category.allow_reviews !== false,
        allow_negotiations: category.allow_negotiations || false,
        default_return_policy: category.default_return_policy || null
      };
      
      await axios.put(
        `/api/categories/${category.id}`,
        updateData,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(category.is_active ? 'הקטגוריה הוסתרה' : 'הקטגוריה הופעלה');
      
      // Small delay to ensure DB transaction completes
      setTimeout(() => {
        fetchCategories();
      }, 300);
    } catch (error) {
      toast.error('שגיאה בעדכון סטטוס הקטגוריה');
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      name_en: '',
      description: '',
      description_en: '',
      slug: '',
      parent_id: null,
      sort_order: 0,
      is_active: true,
      is_featured: false,
      global_attributes: {},
      default_warranty_period: '',
      default_tax_class: 'standard',
      default_shipping_class: '',
      transparency_settings: {
        showStockToB2B: true,
        showStockToB2C: true,
        showCostToB2B: false,
        showCostToB2C: false,
        showSupplierToB2B: false,
        showMarginToAdmin: true
      },
      compliance_requirements: {},
      auto_apply_tags: [],
      visibility_settings: {
        audience: 'both',
        showInMenu: true,
        showInSearch: true
      },
      require_approval: false,
      min_order_quantity: 1,
      max_order_quantity: null,
      allow_reviews: true,
      allow_negotiations: false,
      default_return_policy: ''
    });
    setEditingCategory(null);
  };

  const toggleExpanded = (categoryId) => {
    const newExpanded = new Set(expandedCategories);
    if (newExpanded.has(categoryId)) {
      newExpanded.delete(categoryId);
    } else {
      newExpanded.add(categoryId);
    }
    setExpandedCategories(newExpanded);
  };

  const renderCategoryRow = (category, level = 0) => {
    const hasChildren = category.subcategories && category.subcategories.length > 0;
    const isExpanded = expandedCategories.has(category.id);

    return (
      <React.Fragment key={category.id}>
        <tr className="hover:bg-gray-50">
          <td className="px-6 py-4 whitespace-nowrap">
            <div className="flex items-center" style={{ paddingLeft: `${level * 20}px` }}>
              {hasChildren && (
                <button
                  onClick={() => toggleExpanded(category.id)}
                  className="mr-2 text-gray-400 hover:text-gray-600"
                >
                  {isExpanded ? (
                    <ChevronDownIcon className="h-4 w-4" />
                  ) : (
                    <ChevronRightIcon className="h-4 w-4" />
                  )}
                </button>
              )}
              <div>
                <div className="text-sm font-medium text-gray-900">{category.name}</div>
                <div className="text-sm text-gray-500">{category.name_en}</div>
              </div>
            </div>
          </td>
          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
            {category.slug}
          </td>
          <td className="px-6 py-4 whitespace-nowrap">
            <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
              category.visibility_settings?.audience === 'b2b' 
                ? 'bg-blue-100 text-blue-800'
                : category.visibility_settings?.audience === 'b2c'
                ? 'bg-green-100 text-green-800'
                : 'bg-purple-100 text-purple-800'
            }`}>
              {category.visibility_settings?.audience === 'b2b' ? 'B2B' : 
               category.visibility_settings?.audience === 'b2c' ? 'B2C' : 'Both'}
            </span>
          </td>
          <td className="px-6 py-4 whitespace-nowrap">
            {category.allow_negotiations && (
              <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800 mr-2">
                <ScaleIcon className="h-3 w-3 mr-1" />
                משא ומתן
              </span>
            )}
            {category.require_approval && (
              <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
                <ShieldCheckIcon className="h-3 w-3 mr-1" />
                נדרש אישור
              </span>
            )}
          </td>
          <td className="px-6 py-4 whitespace-nowrap">
            <button
              onClick={() => toggleActive(category)}
              className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                category.is_active
                  ? 'bg-green-100 text-green-800'
                  : 'bg-red-100 text-red-800'
              }`}
            >
              {category.is_active ? (
                <>
                  <EyeIcon className="h-4 w-4 mr-1" />
                  פעיל
                </>
              ) : (
                <>
                  <EyeSlashIcon className="h-4 w-4 mr-1" />
                  לא פעיל
                </>
              )}
            </button>
          </td>
          <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
            <button
              onClick={() => {
                setSelectedCategory(category);
                setShowTransparencyModal(true);
              }}
              className="text-indigo-600 hover:text-indigo-900 mr-3"
              title="הגדרות שקיפות"
            >
              <Cog6ToothIcon className="h-5 w-5" />
            </button>
            <button
              onClick={() => handleEdit(category)}
              className="text-indigo-600 hover:text-indigo-900 mr-3"
            >
              <PencilIcon className="h-5 w-5" />
            </button>
            <button
              onClick={() => handleDelete(category.id)}
              className="text-red-600 hover:text-red-900"
            >
              <TrashIcon className="h-5 w-5" />
            </button>
          </td>
        </tr>
        {hasChildren && isExpanded && category.subcategories.map((subcat) => renderCategoryRow(subcat, level + 1))}
      </React.Fragment>
    );
  };

  if (loading) {
    return <div className="flex justify-center items-center h-64">טוען...</div>;
  }

  const rootCategories = categories.filter(cat => !cat.parent_id);

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-3xl font-bold text-gray-900">ניהול קטגוריות</h1>
        <button
          onClick={() => {
            resetForm();
            setShowModal(true);
          }}
          className="bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2 rounded-lg flex items-center"
        >
          <PlusIcon className="h-5 w-5 mr-2" />
          קטגוריה חדשה
        </button>
      </div>

      <div className="bg-white shadow-md rounded-lg overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                שם
              </th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                Slug
              </th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                קהל יעד
              </th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                תכונות
              </th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                סטטוס
              </th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                פעולות
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {rootCategories.map((category) => renderCategoryRow(category))}
          </tbody>
        </table>
      </div>

      {/* Category Form Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 overflow-y-auto">
          <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-4xl max-h-[90vh] overflow-y-auto m-4">
            <h2 className="text-2xl font-bold mb-4">
              {editingCategory ? 'ערוך קטגוריה' : 'קטגוריה חדשה'}
            </h2>

            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Basic Info */}
              <div className="border-b pb-4">
                <h3 className="text-lg font-semibold mb-3">פרטים בסיסיים</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      שם (עברית) *
                    </label>
                    <input
                      type="text"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      שם (אנגלית)
                    </label>
                    <input
                      type="text"
                      value={formData.name_en}
                      onChange={(e) => setFormData({ ...formData, name_en: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Slug *
                    </label>
                    <input
                      type="text"
                      value={formData.slug}
                      onChange={(e) => setFormData({ ...formData, slug: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      קטגוריה אב
                    </label>
                    <select
                      value={formData.parent_id || ''}
                      onChange={(e) => setFormData({ ...formData, parent_id: e.target.value || null })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md"
                    >
                      <option value="">אין (קטגוריה ראשית)</option>
                      {categories.filter(c => !c.parent_id && c.id !== editingCategory?.id).map(cat => (
                        <option key={cat.id} value={cat.id}>{cat.name}</option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="mt-4">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    תיאור (עברית)
                  </label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    rows="3"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  />
                </div>
              </div>

              {/* Visibility Settings */}
              <div className="border-b pb-4">
                <h3 className="text-lg font-semibold mb-3 flex items-center">
                  <EyeIcon className="h-5 w-5 mr-2" />
                  הגדרות נראות
                </h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      קהל יעד
                    </label>
                    <select
                      value={formData.visibility_settings.audience}
                      onChange={(e) => setFormData({
                        ...formData,
                        visibility_settings: { ...formData.visibility_settings, audience: e.target.value }
                      })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md"
                    >
                      <option value="both">B2B + B2C</option>
                      <option value="b2b">B2B בלבד</option>
                      <option value="b2c">B2C בלבד</option>
                    </select>
                  </div>

                  <div className="flex items-center space-x-4 space-x-reverse">
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        checked={formData.is_active}
                        onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                        className="mr-2"
                      />
                      <span className="text-sm">פעיל</span>
                    </label>

                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        checked={formData.is_featured}
                        onChange={(e) => setFormData({ ...formData, is_featured: e.target.checked })}
                        className="mr-2"
                      />
                      <span className="text-sm">מומלץ</span>
                    </label>
                  </div>
                </div>
              </div>

              {/* Transparency Settings */}
              <div className="border-b pb-4">
                <h3 className="text-lg font-semibold mb-3 flex items-center">
                  <ShieldCheckIcon className="h-5 w-5 mr-2" />
                  הגדרות שקיפות
                </h3>
                <div className="grid grid-cols-2 gap-4">
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={formData.transparency_settings.showStockToB2B}
                      onChange={(e) => setFormData({
                        ...formData,
                        transparency_settings: { ...formData.transparency_settings, showStockToB2B: e.target.checked }
                      })}
                      className="mr-2"
                    />
                    <span className="text-sm">הצג מלאי ל-B2B</span>
                  </label>

                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={formData.transparency_settings.showStockToB2C}
                      onChange={(e) => setFormData({
                        ...formData,
                        transparency_settings: { ...formData.transparency_settings, showStockToB2C: e.target.checked }
                      })}
                      className="mr-2"
                    />
                    <span className="text-sm">הצג מלאי ל-B2C</span>
                  </label>

                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={formData.transparency_settings.showCostToB2B}
                      onChange={(e) => setFormData({
                        ...formData,
                        transparency_settings: { ...formData.transparency_settings, showCostToB2B: e.target.checked }
                      })}
                      className="mr-2"
                    />
                    <span className="text-sm">הצג עלות ל-B2B</span>
                  </label>

                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={formData.transparency_settings.showSupplierToB2B}
                      onChange={(e) => setFormData({
                        ...formData,
                        transparency_settings: { ...formData.transparency_settings, showSupplierToB2B: e.target.checked }
                      })}
                      className="mr-2"
                    />
                    <span className="text-sm">הצג ספק ל-B2B</span>
                  </label>
                </div>
              </div>

              {/* Control Settings */}
              <div className="border-b pb-4">
                <h3 className="text-lg font-semibold mb-3 flex items-center">
                  <Cog6ToothIcon className="h-5 w-5 mr-2" />
                  הגדרות בקרה
                </h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      כמות הזמנה מינימלית
                    </label>
                    <input
                      type="number"
                      value={formData.min_order_quantity}
                      onChange={(e) => setFormData({ ...formData, min_order_quantity: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md"
                      min="1"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      כמות הזמנה מקסימלית
                    </label>
                    <input
                      type="number"
                      value={formData.max_order_quantity || ''}
                      onChange={(e) => setFormData({ ...formData, max_order_quantity: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      תקופת אחריות (ימים)
                    </label>
                    <input
                      type="number"
                      value={formData.default_warranty_period}
                      onChange={(e) => setFormData({ ...formData, default_warranty_period: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      מחלקת מס ברירת מחדל
                    </label>
                    <select
                      value={formData.default_tax_class}
                      onChange={(e) => setFormData({ ...formData, default_tax_class: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md"
                    >
                      <option value="standard">רגיל</option>
                      <option value="reduced">מופחת</option>
                      <option value="zero">אפס</option>
                      <option value="exempt">פטור</option>
                    </select>
                  </div>
                </div>

                <div className="mt-4 flex items-center space-x-4 space-x-reverse">
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={formData.allow_reviews}
                      onChange={(e) => setFormData({ ...formData, allow_reviews: e.target.checked })}
                      className="mr-2"
                    />
                    <span className="text-sm">אפשר ביקורות</span>
                  </label>

                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={formData.allow_negotiations}
                      onChange={(e) => setFormData({ ...formData, allow_negotiations: e.target.checked })}
                      className="mr-2"
                    />
                    <span className="text-sm">אפשר משא ומתן</span>
                  </label>

                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={formData.require_approval}
                      onChange={(e) => setFormData({ ...formData, require_approval: e.target.checked })}
                      className="mr-2"
                    />
                    <span className="text-sm">דרוש אישור מנהל</span>
                  </label>
                </div>
              </div>

              {/* Auto-Apply Tags */}
              <div>
                <h3 className="text-lg font-semibold mb-3 flex items-center">
                  <TagIcon className="h-5 w-5 mr-2" />
                  תגיות אוטומטיות
                </h3>
                <input
                  type="text"
                  placeholder="הזן תגיות מופרדות בפסיקים"
                  value={formData.auto_apply_tags.join(', ')}
                  onChange={(e) => setFormData({
                    ...formData,
                    auto_apply_tags: e.target.value.split(',').map(tag => tag.trim()).filter(Boolean)
                  })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                />
                <p className="text-xs text-gray-500 mt-1">
                  תגיות אלו יתווספו אוטומטית לכל המוצרים בקטגוריה זו
                </p>
              </div>

              <div className="flex justify-end space-x-2 space-x-reverse pt-4">
                <button
                  type="button"
                  onClick={() => {
                    setShowModal(false);
                    resetForm();
                  }}
                  className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                >
                  ביטול
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700"
                >
                  {editingCategory ? 'עדכן' : 'צור'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Transparency Details Modal */}
      {showTransparencyModal && selectedCategory && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-2xl">
            <h2 className="text-2xl font-bold mb-4">הגדרות שקיפות - {selectedCategory.name}</h2>
            
            <div className="space-y-4">
              <div>
                <h3 className="font-semibold mb-2">הגדרות נראות:</h3>
                <ul className="list-disc list-inside space-y-1 text-sm">
                  <li>קהל יעד: {selectedCategory.visibility_settings?.audience === 'both' ? 'כולם' : selectedCategory.visibility_settings?.audience?.toUpperCase()}</li>
                  <li>הצג במלאי ל-B2B: {selectedCategory.transparency_settings?.showStockToB2B ? 'כן' : 'לא'}</li>
                  <li>הצג במלאי ל-B2C: {selectedCategory.transparency_settings?.showStockToB2C ? 'כן' : 'לא'}</li>
                </ul>
              </div>

              <div>
                <h3 className="font-semibold mb-2">הגדרות בקרה:</h3>
                <ul className="list-disc list-inside space-y-1 text-sm">
                  <li>כמות הזמנה מינימלית: {selectedCategory.min_order_quantity || 1}</li>
                  {selectedCategory.max_order_quantity && (
                    <li>כמות הזמנה מקסימלית: {selectedCategory.max_order_quantity}</li>
                  )}
                  <li>אפשר ביקורות: {selectedCategory.allow_reviews ? 'כן' : 'לא'}</li>
                  <li>אפשר משא ומתן: {selectedCategory.allow_negotiations ? 'כן' : 'לא'}</li>
                  <li>דרוש אישור: {selectedCategory.require_approval ? 'כן' : 'לא'}</li>
                </ul>
              </div>

              {selectedCategory.auto_apply_tags && selectedCategory.auto_apply_tags.length > 0 && (
                <div>
                  <h3 className="font-semibold mb-2">תגיות אוטומטיות:</h3>
                  <div className="flex flex-wrap gap-2">
                    {selectedCategory.auto_apply_tags.map((tag, index) => (
                      <span key={index} className="px-2 py-1 bg-blue-100 text-blue-800 rounded-full text-xs">
                        {tag}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <div className="flex justify-end mt-6">
              <button
                onClick={() => setShowTransparencyModal(false)}
                className="px-4 py-2 bg-gray-200 text-gray-800 rounded-md hover:bg-gray-300"
              >
                סגור
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminCategories;
